'use client';

/**
 * Fires a productClick event for Google Analytics. *Note: Do not ever push PII into the data layer!!*
 *
 * @example
 * const { trackEvent } = useProductClickEvent();
 * trackEvent({
        dealerId: dealerCode,
        products: [productData],
        list: ORDER_STATUS_ITEM_LINK_GA_LIST_NAME,
        model: equipmentModel
      );
 */

import { useCallback } from 'react';
import { sendGTMEvent } from '@next/third-parties/google';
import {
  DEFAULT_BUSINESS_UNIT,
  EVENT_PRODUCT_CLICK
} from 'constants/analyticsConstants';
import { getValueOrElseEmptyString } from 'utils/string';
import { useTranslations } from 'next-intl';
import { getInventoryStatus } from 'utils/analytics';
interface ProductClickEventProps {
  cartId?: string;
  currencyCode?: string;
  dealerId?: string | number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  products: any[];
  list: string;
  model?: string;
  paymentMethods?: string[];
  storeName: string;
}

const DEFAULT_BRAND = 'Cat';

export const useProductClickEvent = () => {
  const t = useTranslations();
  const CONTACT_YOUR_DEALER = t('CONTACT_YOUR_DEALER');

  const buildEvent = useCallback(
    ({
      cartId,
      currencyCode,
      dealerId,
      products,
      list,
      model,
      paymentMethods,
      storeName
    }: ProductClickEventProps) => {
      const cartDetails = {
        businessUnit: DEFAULT_BUSINESS_UNIT,
        cartId: getValueOrElseEmptyString(cartId),
        dealerId: getValueOrElseEmptyString(dealerId),
        paymentMethod: (paymentMethods ?? []).join(', ')
      };

      const productsMapped = products.map((item, index) => {
        const productName = item.name ?? item.partName;

        return {
          brand: DEFAULT_BRAND,
          businessUnit: DEFAULT_BUSINESS_UNIT,
          category: getValueOrElseEmptyString(item.category),
          checkFitment: '',
          classification: getValueOrElseEmptyString(item.classification),
          id: getValueOrElseEmptyString(item.partNumber),
          inventoryLocation: getValueOrElseEmptyString(storeName),
          inventoryStatus: getValueOrElseEmptyString(
            getInventoryStatus(
              CONTACT_YOUR_DEALER,
              item.availability,
              item.sourcingDetails
            )
          ),
          kitId: getValueOrElseEmptyString(item.kitId),
          msrp: getValueOrElseEmptyString(item.msrp),
          name: getValueOrElseEmptyString(productName ?? item.description),
          packageFlag: getValueOrElseEmptyString(item.packageFlag),
          partNumber: getValueOrElseEmptyString(item.partNumber),
          position: index + 1,
          price: getValueOrElseEmptyString(
            item?.unformattedUnitPrice ||
              item?.priceWithoutGST?.formatted?.unitPrice
          ),
          productAttributesVisible: getValueOrElseEmptyString(
            item.productAttributesVisible
          ),
          productWeight: getValueOrElseEmptyString(item.weight),
          quantity: getValueOrElseEmptyString(item.quantity),
          relatedModel: getValueOrElseEmptyString(item.model || model)
        };
      });

      return {
        cartDetails,
        ecommerce: {
          currencyCode: getValueOrElseEmptyString(currencyCode),
          click: {
            actionField: { list },
            products: productsMapped
          }
        },
        event: EVENT_PRODUCT_CLICK
      };
    },
    [CONTACT_YOUR_DEALER]
  );

  const trackEvent = useCallback(
    (data: ProductClickEventProps) => sendGTMEvent(buildEvent(data)),
    [buildEvent]
  );

  return { trackEvent };
};
